import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ProspectsManagerProspectsTable } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-prospects-table';
import { ProspectsManagerRepsTable } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/prospects-manager-reps-table';
import { ScrollView, View } from 'react-native';
import { XStack, YStack } from '@usepepper/sprout-kit-ui';
import type {
  ManagerProspectRowData,
  ManagerRepRowData,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';

const CARD_MAX_HEIGHT = 620;
const CARD_MIN_HEIGHT = 480;
const CARD_MIN_WIDTH = 360;

interface ProspectsManagerViewProps {
  filters: React.ReactNode;
  forecast: React.ReactNode;
  hasProspectsError: boolean;
  hasRepsError: boolean;
  isProspectsLoading: boolean;
  isRepsLoading: boolean;
  onProspectRowClick: (row: ManagerProspectRowData) => void;
  onProspectsRetry: () => void;
  onRepsRetry: () => void;
  pipelineSummary: React.ReactNode;
  prospects: ManagerProspectRowData[];
  prospectsEmptyComponent?: React.ReactNode;
  prospectsToolbar?: React.ReactNode;
  reps: ManagerRepRowData[];
  repsEmptyComponent?: React.ReactNode;
  repsToolbar?: React.ReactNode;
}

export const ProspectsManagerView: React.FC<ProspectsManagerViewProps> = ({
  filters,
  forecast,
  hasProspectsError,
  hasRepsError,
  isProspectsLoading,
  isRepsLoading,
  onProspectRowClick,
  onProspectsRetry,
  onRepsRetry,
  pipelineSummary,
  prospects,
  prospectsEmptyComponent,
  prospectsToolbar,
  reps,
  repsEmptyComponent,
  repsToolbar,
}) => {
  const [styles] = useStyles();

  return (
    <ScrollView contentContainerStyle={styles.scrollContent}>
      <YStack
        flex={1}
        gap="$16"
        paddingBottom="$24"
        paddingHorizontal="$24"
        paddingTop="$24"
      >
        {filters}

        {forecast}

        {pipelineSummary}

        <XStack flex={1} flexWrap="wrap" gap="$16">
          <View
            accessibilityLabel={i18n.t(
              'sales_portal.prospects.manager.prospects_heading',
            )}
            role="region"
            style={styles.card}
          >
            <ProspectsManagerProspectsTable
              data={prospects}
              emptyComponent={prospectsEmptyComponent}
              hasError={hasProspectsError}
              isLoading={isProspectsLoading}
              onRetry={onProspectsRetry}
              onRowClick={onProspectRowClick}
              toolbar={prospectsToolbar}
            />
          </View>
          <View
            accessibilityLabel={i18n.t(
              'sales_portal.prospects.manager.reps_heading',
            )}
            role="region"
            style={styles.card}
          >
            <ProspectsManagerRepsTable
              data={reps}
              emptyComponent={repsEmptyComponent}
              hasError={hasRepsError}
              isLoading={isRepsLoading}
              onRetry={onRepsRetry}
              toolbar={repsToolbar}
            />
          </View>
        </XStack>
      </YStack>
    </ScrollView>
  );
};

const useStyles = createStyles(() => ({
  card: {
    flex: 1,
    maxHeight: CARD_MAX_HEIGHT,
    minHeight: CARD_MIN_HEIGHT,
    minWidth: CARD_MIN_WIDTH,
  },
  scrollContent: {
    flexGrow: 1,
  },
}));
